import React, { Suspense, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Empty, message, Row, Space } from 'antd';
import { ArrowLeftOutlined, CopyOutlined } from '@ant-design/icons';
import { Main } from './styled';
import Spinner from '../components/utilities/Spinner';
import { DataService } from '../config/data/dataService';
import AssessmentDiagnosticsCard from '../components/historyDetail/AssessmentDiagnosticsCard';
import DevicePhotosCard from '../components/historyDetail/DevicePhotosCard';
import HistoryDetailHeader from '../components/historyDetail/HistoryDetailHeader';
import PickupPaymentDetailsCard from '../components/historyDetail/PickupPaymentDetailsCard';
import ProposalSummaryCard from '../components/historyDetail/ProposalSummaryCard';
import {
  DetailsContainer,
  CommandBar,
  CommandLeft,
  BackButton,
  RefPill,
  StatusPill,
  ValuationMiniPill,
} from '../components/historyDetail/HistoryDetail.styles';

const parseDeviceImages = (deviceImage) => {
  if (!deviceImage) return [];
  if (Array.isArray(deviceImage)) return deviceImage;

  const value = String(deviceImage).trim();

  try {
    const parsedImages = JSON.parse(value);
    if (Array.isArray(parsedImages)) return parsedImages;
  } catch (error) {
  }

  const markdownLinkMatch = value.match(/\((https?:\/\/.*)\)$/);
  const imageList = markdownLinkMatch ? markdownLinkMatch[1] : value.replace(/^\[/, '').replace(/\]$/, '');

  return imageList
    .split(',')
    .map((url) => url.trim())
    .filter((url) => url.startsWith('http'));
};

const parseAnswers = (answers, labels = {}) => {
  if (!answers) return [];

  try {
    const parsedAnswers = typeof answers === 'string' ? JSON.parse(answers) : answers;
    return Object.keys(parsedAnswers || {})
      .sort((a, b) => Number(a) - Number(b))
      .map((answerId) => ({
        id: Number(answerId),
        question: labels[answerId] || `Question ${answerId}`,
        answer: parsedAnswers[answerId],
      }));
  } catch (error) {
    console.warn('Unable to parse history detail answers:', error);
    return [];
  }
};

const getHighestBidAmount = (data) => {
  const amount = Number(data.highest_bid_amount ?? 0);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

const formatBidStatus = (status) => {
  if (!status) return 'Pending';
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const mapHistoryDetailRow = (row, labels) => ({
  id: row.reference_no || row.exchange_reference_id,
  referenceNo: row.reference_no || row.exchange_reference_id,
  storeCode: row.store_code,
  storeName: row.store_name,
  storeAddress: row.address,
  storeEmail: row.email,
  storeMobile: row.mobile,
  customerName: row.customer_name || '-',
  customerMobile: row.customer_mobile,
  imeiNumber: row.imei_number,
  deviceModel: [row.brand, row.model].filter(Boolean).join(' ') || row.model || '-',
  bidAmount: row.bid_amount,
  highestBid: getHighestBidAmount(row),
  bidStatus: row.bid_status,
  status: formatBidStatus(row.bid_status),
  rawStatus: row.status,
  date: row.evb_created_at || row.er_created_at || row.created_at || '-',
  createdAt: row.er_created_at,
  updatedAt: row.er_updated_at,
  bidCreatedAt: row.evb_created_at,
  bidUpdatedAt: row.evb_updated_at,
  vendorCode: row.vendor_code_id,
  exchangeReferenceId: row.exchange_reference_id,
  quotedAmount: Number(row.bid_amount) || 0,
  initialQuote: Number(row.bid_amount) || 0,
  condition: String(row.device_condition ?? '').trim() || '-',
  answersRaw: row.answers,
  images: parseDeviceImages(row.device_image),
  checkDetails: parseAnswers(row.answers, labels),
});

const HistoryDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const referenceNo = location.state?.referenceNo;
  const [activeImgIndex, setActiveImgIndex] = useState(0);
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchHistoryDetail = async () => {
      setLoading(true);
      if (!referenceNo) {
        setApp(null);
        setLoading(false);
        return;
      }
      try {
        const questionsResponse = await DataService.get('/getVendorQuestions');
        if (questionsResponse.data.status !== true) {
          throw new Error(questionsResponse.data.message || 'Unable to fetch vendor questions');
        }
        const labels = Object.fromEntries(
          questionsResponse.data.data.map(({ id, question }) => [id, question]),
        );

        let page = 1;
        let foundRow = null;
        let totalPages = 1;
        do {
          const response = await DataService.post('/getHistoryList', {
            page: String(page),
            page_items_count: 10,
            search: '',
          });
          if (cancelled) return;
          if (response.data.status !== true) {
            throw new Error(response.data.message || 'Unable to fetch history details');
          }
          const responseData = response.data.data;
          foundRow = responseData.data.find((row) => (
            row.reference_no === referenceNo || row.exchange_reference_id === referenceNo
          ));
          totalPages = Math.ceil(Number(responseData.total_count) / 10);
          page += 1;
        } while (!foundRow && page <= totalPages);
        setApp(foundRow ? mapHistoryDetailRow(foundRow, labels) : null);
      } catch (error) {
        if (cancelled) return;
        console.error('History detail fetch error:', error);
        message.error('Unable to fetch quote details');
        setApp(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchHistoryDetail();
    return () => { cancelled = true; };
  }, [referenceNo]);

  if (loading) {
    return (
      <Main>
        <Spinner />
      </Main>
    );
  }

  if (!app) {
    return (
      <Main>
        <Suspense fallback={<Spinner />}>
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/History')}
              style={{ marginBottom: '24px', borderRadius: '6px' }}
            >
              Back to Quote History
            </Button>
            <Empty description="Quote Application Details Not Found" />
          </div>
        </Suspense>
      </Main>
    );
  }

  const copyToClipboard = (text, label = 'Reference number') => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard!`);
  };

  return (
    <Main>
      <Suspense fallback={<Spinner />}>
        <DetailsContainer style={{ padding: '24px 20px', width: '100%' }}>
          <CommandBar>
            <CommandLeft>
              <BackButton type="button" onClick={() => navigate(-1)}>
                <ArrowLeftOutlined /> Back to Quote History
              </BackButton>
              {app.referenceNo && (
                <RefPill
                  title="Click to copy Reference Number"
                  onClick={() => copyToClipboard(app.referenceNo, 'Reference number')}
                >
                  <CopyOutlined style={{ fontSize: 13, color: '#6366f1' }} />
                  <span>REF: {app.referenceNo}</span>
                </RefPill>
              )}
              <StatusPill $status={String(app.bidStatus || app.rawStatus || '').toLowerCase()}>
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: 'currentColor',
                    display: 'inline-block',
                  }}
                />
                {app.status || 'Active'}
              </StatusPill>
            </CommandLeft>

            <ValuationMiniPill $isAccepted={String(app.bidStatus).toLowerCase() === 'accepted'}>
              <span className="pill-label">Your Bid:</span>
              <span className="pill-val">₹{Number(app.bidAmount || app.quotedAmount || 0).toLocaleString('en-IN')}</span>
            </ValuationMiniPill>
          </CommandBar>

          <HistoryDetailHeader app={app} />

          <Row gutter={[24, 24]}>
            <Col xs={24} lg={15}>
              <AssessmentDiagnosticsCard app={app} />
              <PickupPaymentDetailsCard app={app} />
            </Col>

            <Col xs={24} lg={9}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <DevicePhotosCard
                  images={app.images}
                  activeImgIndex={activeImgIndex}
                  onImageSelect={setActiveImgIndex}
                />
                <ProposalSummaryCard app={app} />
              </Space>
            </Col>
          </Row>
        </DetailsContainer>
      </Suspense>
    </Main>
  );
};

export default HistoryDetail;
